import React, { useCallback, useEffect, useRef, useState } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import { useToast } from "src/hooks/use-toast";
import type { StopJobRunMutation as StopJobRunMutationType } from "src/pages/DigestPage/mutations/__generated__/StopJobRunMutation.graphql";
import { StopJobRunMutation } from "src/pages/DigestPage/mutations/StopJobRunMutation";
import { getDigestInputSections } from "src/pages/DigestPage/utils/digestInputSections";

import { DigestJobRunList_job$key } from "./__generated__/DigestJobRunList_job.graphql";
import DigestJobRunRow from "./DigestJobRunRow";

const digestJobRunListFragment = graphql`
  fragment DigestJobRunList_job on GraphQLJob {
    id
    name
    schedule
    inputJson
    jobRuns(limit: 20, sortBy: "started_at", sortOrder: "desc") {
      edges {
        node {
          id
          status
          createdAt
          startedAt
          completedAt
          updatedAt
        }
      }
    }
  }
`;

interface DigestJobRunListProps {
  job: DigestJobRunList_job$key;
  onRefresh: () => void;
}

interface ScheduledRunType {
  id: string;
  status: "SCHEDULED";
  scheduledRunDate: number;
  createdAt: string;
  startedAt: null;
  completedAt: null;
  updatedAt: string;
}

const DigestJobRunList: React.FC<DigestJobRunListProps> = React.memo(function DigestJobRunList({
  job: jobData,
  onRefresh,
}) {
  const job = useFragment(digestJobRunListFragment, jobData);
  const actualJobRuns = job.jobRuns.edges.map(edge => edge.node);
  const { toast } = useToast();
  const [stopJobRun, isStopInFlight] = useMutation<StopJobRunMutationType>(StopJobRunMutation);
  const [cancelJobRunId, setCancelJobRunId] = useState<string | null>(null);

  const handleConfirmCancel = () => {
    if (!cancelJobRunId) return;
    stopJobRun({
      variables: { input: { jobRunId: cancelJobRunId } },
      onCompleted: (response) => {
        setCancelJobRunId(null);
        if (response.stopJobRun?.success) {
          toast({ title: "Digest run cancelled", description: "The digest run has been stopped.", variant: "success" });
          setTimeout(() => onRefresh(), 1000);
        } else {
          toast({ title: "Error", description: "Failed to cancel the digest run. Please try again.", variant: "error" });
        }
      },
      onError: (error) => {
        setCancelJobRunId(null);
        toast({ title: "Error", description: error.message || "An unexpected error occurred", variant: "error" });
      },
    });
  };

  // Track which scheduled runs we're actively monitoring for transition to actual runs
  const [monitoringScheduledRuns, setMonitoringScheduledRuns] = useState<Set<number>>(new Set());
  const timersRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  // Extract scheduled runs from run_dates in schedule
  const scheduledRuns: ScheduledRunType[] = React.useMemo(() => {
    const schedule = job.schedule as any;
    if (!schedule || !schedule.run_dates) {
      return [];
    }

    const runDates = schedule.run_dates;
    if (!Array.isArray(runDates)) {
      return [];
    }

    const now = Math.floor(Date.now() / 1000);

    // Filter for future run_dates only
    return runDates
      .filter((timestamp: number) => timestamp > now)
      .map((timestamp: number) => ({
        id: `scheduled-${timestamp}`,
        status: "SCHEDULED" as const,
        scheduledRunDate: timestamp,
        createdAt: new Date(timestamp * 1000).toISOString(),
        startedAt: null,
        completedAt: null,
        updatedAt: new Date(timestamp * 1000).toISOString(),
      }));
  }, [job.schedule]);

  // Helper function to check if a scheduled run has a matching actual run
  // Match criteria: any actual run with start time within 2 minutes of scheduled time
  const hasMatchingActualRun = useCallback((scheduledTimestamp: number): boolean => {
    const scheduledTime = scheduledTimestamp * 1000; // Convert to milliseconds
    const matchWindow = 2 * 60 * 1000; // 2 minutes in milliseconds

    return actualJobRuns.some((run: any) => {
      // Check if the run's start time is close to the scheduled time
      const runTime = run.startedAt ? new Date(run.startedAt).getTime() : new Date(run.createdAt).getTime();
      const timeDiff = Math.abs(runTime - scheduledTime);

      return timeDiff <= matchWindow;
    });
  }, [actualJobRuns]);

  // Filter out scheduled runs that have matching actual runs
  const filteredScheduledRuns = React.useMemo(() => {
    return scheduledRuns.filter(scheduledRun => !hasMatchingActualRun(scheduledRun.scheduledRunDate));
  }, [scheduledRuns, hasMatchingActualRun]);

  // Merge actual runs and filtered scheduled runs, then sort by start time
  const allRuns = React.useMemo(() => {
    const combined = [...actualJobRuns, ...filteredScheduledRuns];
    return combined.sort((a, b) => {
      const timeA = a.startedAt || a.createdAt;
      const timeB = b.startedAt || b.createdAt;
      return new Date(timeB).getTime() - new Date(timeA).getTime();
    });
  }, [actualJobRuns, filteredScheduledRuns]);

  // Set up timers to refetch after scheduled runs to check for matching actual runs
  useEffect(() => {
    const now = Date.now();

    filteredScheduledRuns.forEach((scheduledRun) => {
      const scheduledTime = scheduledRun.scheduledRunDate * 1000; // Convert to milliseconds
      const checkTime = scheduledTime + 60 * 1000; // 1 minute after scheduled time

      // Skip if we're already monitoring this scheduled run
      if (monitoringScheduledRuns.has(scheduledRun.scheduledRunDate)) {
        return;
      }

      // If the check time is in the future, set up initial timer
      if (checkTime > now) {
        const delay = checkTime - now;
        const timer = setTimeout(() => {
          // Start monitoring this scheduled run
          setMonitoringScheduledRuns(prev => new Set(prev).add(scheduledRun.scheduledRunDate));
          // Refetch to check for matching run
          onRefresh();
        }, delay);

        timersRef.current.set(scheduledRun.scheduledRunDate, timer);
      }
      // If check time has passed but we haven't found a match, start monitoring now
      else if (checkTime <= now) {
        setMonitoringScheduledRuns(prev => new Set(prev).add(scheduledRun.scheduledRunDate));
      }
    });

    // Clean up timers for scheduled runs that are no longer in the list
    const currentScheduledTimestamps = new Set(filteredScheduledRuns.map(sr => sr.scheduledRunDate));
    timersRef.current.forEach((timer, timestamp) => {
      if (!currentScheduledTimestamps.has(timestamp)) {
        clearTimeout(timer);
        timersRef.current.delete(timestamp);
        setMonitoringScheduledRuns(prev => {
          const next = new Set(prev);
          next.delete(timestamp);
          return next;
        });
      }
    });

    // Cleanup on unmount
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, [filteredScheduledRuns, monitoringScheduledRuns, onRefresh]);

  // Set up interval to refetch every minute for scheduled runs we're monitoring
  useEffect(() => {
    if (monitoringScheduledRuns.size === 0) {
      return;
    }

    const interval = setInterval(() => {
      // Check if we still have scheduled runs being monitored
      const stillMonitoring = filteredScheduledRuns.some(sr => monitoringScheduledRuns.has(sr.scheduledRunDate));

      if (stillMonitoring) {
        onRefresh();
      }
    }, 60 * 1000); // Refetch every minute

    return () => clearInterval(interval);
  }, [monitoringScheduledRuns, filteredScheduledRuns, onRefresh]);

  return (
    <div className="overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="px-3 py-2 text-left text-sm font-medium text-muted-foreground w-[40px] sm:w-[20%]">
              <span className="hidden sm:inline">Status</span>
            </th>
            <th className="px-3 py-2 text-left text-sm font-medium text-muted-foreground">
              Start Time
            </th>
            <th className="px-3 py-2 text-left text-sm font-medium text-muted-foreground w-[20%] hidden sm:table-cell">
              Duration
            </th>
          </tr>
        </thead>
        <tbody>
          {allRuns.length > 0 ? (
            allRuns.map((jobRun) => (
              <DigestJobRunRow
                key={jobRun.id}
                jobRun={jobRun}
                jobId={job.id}
                onRefresh={onRefresh}
                onCancel={setCancelJobRunId}
                totalSections={getDigestInputSections(job.inputJson).length}
              />
            ))
          ) : (
            <tr>
              <td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">
                No job runs found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <UIConfirmationDialog
        isOpen={cancelJobRunId !== null}
        onClose={() => setCancelJobRunId(null)}
        onConfirm={handleConfirmCancel}
        title="Cancel digest run"
        description="Are you sure you want to cancel this digest run? Any completed sections will be preserved, but remaining sections will not be processed."
        confirmText={isStopInFlight ? "Cancelling..." : "Cancel run"}
        cancelText="Keep running"
        variant="destructive"
        isLoading={isStopInFlight}
      />
    </div>
  );
});

export default DigestJobRunList;